import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Slider from '@mui/material/Slider';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { BottomHeaderWrapper, HeaderWrapper, LogoWrapper, TopHeaderWrapper } from './Header.style';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { DividedEachPageState } from 'store/features/context/types';
import { decrement, increment, selecZoom, setZoom } from 'store/features/zoom/zoomSlice';
import { selectFiles } from '../../store/features/files/selectors';
import { useMemo, ChangeEvent, useEffect } from 'react';
import {
    checkIsPageModified,
    checkIsPageModifiedIndexOrSplit,
    mergeAllAction,
    restoreDefaultAction,
    splitAllAction
} from '../../store/features/pages/actions';
import { ModalType } from 'components/Dialogs/types';
import { createNewFiles } from 'store/features/newFiles/actions';
import { selectPages } from 'store/features/pages/selectors';
import { useBeforeUnload } from 'react-use';
import useWindowSize from 'hooks/useWindowSize';
import { resetDividedEachPageAction, toggleDividedEachPageAction } from '../../store/features/context/actions';
import { selectDividedEachPage } from '../../store/features/context/selectors';
import AddFileHeaderButton from 'components/AddFileButton/AddFileHeaderButton';
import { addFilesInputHandler } from 'helpers';
import { closeWizard } from '../../api/api';

const Header = ({ toggleModalOpen }: { toggleModalOpen: (type: ModalType) => void }) => {
    const dispatch = useAppDispatch();

    const pages = useAppSelector(selectPages);
    const files = useAppSelector(selectFiles);

    const { width: windowWidth } = useWindowSize();

    const isDisabled = useMemo(
        () =>
            Object.values(pages.entities).every(page => {
                const file = files[page!.sourceFileId]!;
                if (!file.refId) return false;
                if (file.pagesIds.length > 1) {
                    return !checkIsPageModified(page!);
                } else {
                    return !checkIsPageModifiedIndexOrSplit(page!);
                }
            }),
        [files, pages.entities]
    );

    useEffect(() => {
        const closeWizardOnEscapeClick = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isDisabled) {
                    closeWizard();
                } else toggleModalOpen('close');
            }
        };

        document.addEventListener('keydown', closeWizardOnEscapeClick, false);

        return () => document.removeEventListener('keydown', closeWizardOnEscapeClick, false);
    }, [isDisabled, toggleModalOpen]);

    useBeforeUnload(!isDisabled, 'Masz niezapisane dokumenty. Czy na pewno chcesz zakończyć?');

    const removedEveryPage = Object.values(pages.entities).every(page => page?.hidden === true);

    const zoomValue = useAppSelector(selecZoom);

    const fileNames = useMemo(
        () =>
            Object.values(files)
                .map(val => val?.fileName)
                .join(', '),
        [files]
    );

    const handleChange = (event: Event, newValue: number | number[]) => {
        dispatch(setZoom({ value: newValue as number }));
    };

    const handleRestoreClick = () => {
        dispatch(restoreDefaultAction());
        dispatch(resetDividedEachPageAction());
    };

    const dividedEachPage = useAppSelector(selectDividedEachPage);

    return (
        <HeaderWrapper elevation={0} position='static'>
            <TopHeaderWrapper>
                <LogoWrapper style={{ maxWidth: windowWidth < 1420 ? '1220px' : '1376px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                            variant='h1'
                            component='div'
                            sx={{ color: 'rgba(0,0,0,0.87)', fontSize: '16px', lineHeight: '28px', fontWeight: '400' }}
                        >
                            Połącz lub podziel pliki
                        </Typography>
                        <Box
                            sx={{ borderRight: '2px solid rgba(189, 189, 189, 1)', height: '32px', margin: '0 16px' }}
                        ></Box>
                        <AddFileHeaderButton
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                addFilesInputHandler(e.target.files, dispatch, e)
                            }
                        />
                        <Tooltip title={fileNames}>
                            <Typography
                                variant='h6'
                                component='div'
                                sx={{
                                    color: '#5C5C5C',
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    fontWeight: '400',
                                    maxWidth: '469px',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {fileNames}
                            </Typography>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            variant='text'
                            sx={{
                                fontSize: '14px',
                                lineHeight: '24px',
                                padding: '6px 16px',
                                textTransform: 'none',
                                marginRight: '8px'
                            }}
                            onClick={isDisabled ? closeWizard : () => toggleModalOpen('close')}
                        >
                            Anuluj
                        </Button>
                        <Button
                            variant='contained'
                            sx={{ fontSize: '14px', lineHeight: '24px', padding: '6px 16px', textTransform: 'none' }}
                            disabled={isDisabled || removedEveryPage}
                            onClick={() => {
                                dispatch(createNewFiles());
                                toggleModalOpen('save');
                            }}
                        >
                            Kontynuuj
                        </Button>
                    </Box>
                </LogoWrapper>
            </TopHeaderWrapper>

            <BottomHeaderWrapper>
                <Box sx={{ '& label span': { fontSize: '14px', lineHeight: '20px' } }}>
                    <FormControlLabel
                        control={<Checkbox size='small' />}
                        label='Podziel po każdej stronie'
                        checked={dividedEachPage === DividedEachPageState.TRUE ? true : false}
                        onChange={() => {
                            if (dividedEachPage === DividedEachPageState.TRUE) {
                                dispatch(mergeAllAction());
                            } else {
                                dispatch(splitAllAction());
                            }
                            dispatch(toggleDividedEachPageAction({ from: 'header' }));
                        }}
                    />
                    <Button
                        variant='text'
                        sx={{ textTransform: 'none', color: 'rgba(0,0,0,0.87)' }}
                        onClick={handleRestoreClick}
                    >
                        Przywróć domyślne
                    </Button>
                </Box>
                <Box display='flex' alignItems='center'>
                    <Box sx={{ width: '224px' }}>
                        <Stack spacing={1} direction='row' alignItems='center'>
                            <ZoomOutIcon onClick={() => dispatch(decrement())} style={{ cursor: 'pointer' }} />
                            <Slider
                                aria-label='zoom'
                                value={zoomValue}
                                onChange={handleChange}
                                max={2.6}
                                min={0.6}
                                step={0.4}
                            />
                            <ZoomInIcon onClick={() => dispatch(increment())} style={{ cursor: 'pointer' }} />
                        </Stack>
                    </Box>
                </Box>
            </BottomHeaderWrapper>
        </HeaderWrapper>
    );
};

export default Header;
